import { allOf, answerEquals, anyOf, formResult, questionResult, signalEquals, } from './equation.js';
import { RUNTIME_EVENTS, SCRATCHPAD_EVENTS } from './graph.js';
import { startConversationOpening } from './template-openings.js';
/**
 * The owner-lead-discovery template: a form node for the four discovery
 * questions, three fixed exits to downstream nodes. One source of truth
 * consumed by the admin "Create from template" flow and by any contract
 * test that wants a known-good form graph.
 */
export const OWNER_LEAD_GRAPH_TEMPLATE_ID = '00000000-0000-4000-8000-000000012101';
export const OWNER_LEAD_FORM_NODE_ID = '00000000-0000-4000-8000-00000001e2d5';
const START_ID = '00000000-0000-4000-8000-00000001d001';
const GREET_ID = '00000000-0000-4000-8000-00000001a001';
const SCHEDULE_ID = '00000000-0000-4000-8000-0000000152c2';
const END_CONFIRM_ID = '00000000-0000-4000-8000-00000001ea17';
const END_HANDOFF_ID = '00000000-0000-4000-8000-00000001ff14';
const END_DNC_ID = '00000000-0000-4000-8000-00000001dcb0';
const OPENING = startConversationOpening({
    startNodeId: START_ID,
    entryEdgeId: '00000000-0000-4000-8000-00000000e001',
    exitEdgeId: '00000000-0000-4000-8000-00000000e012',
    toNodeId: GREET_ID,
});
export const OWNER_LEAD_GRAPH_TEMPLATE = {
    name: 'Owner lead scheduling',
    persona: 'You are Alex, a virtual assistant calling owners who recently asked about ' +
        'property management services. Be warm, natural, and concise: one or two ' +
        'short spoken sentences per turn, one question per turn.',
    active: true,
    knowledgeBaseIds: [],
    watchEvents: [SCRATCHPAD_EVENTS.DO_NOT_CALL],
    nodes: [
        OPENING.node,
        {
            id: GREET_ID,
            type: 'say',
            title: 'Greeting',
            position: 1,
            text: 'Hi, this is Alex calling about the property management services you ' +
                'asked about. I just have a few quick questions, then I can get you ' +
                'set up with one of our market experts.',
        },
        {
            id: OWNER_LEAD_FORM_NODE_ID,
            type: 'form',
            tools: [],
            knowledgeBaseIds: [],
            excludedKnowledgeBaseIds: [],
            title: 'Owner lead discovery',
            position: 2,
            items: [
                {
                    kind: 'question',
                    id: 'property_zip',
                    text: "What's the zip code for the property you'd like managed?",
                    answerType: { kind: 'zip' },
                    required: true,
                    mode: 'verbatim',
                    depth: 0,
                },
                {
                    kind: 'prompt',
                    id: 'company_pitch',
                    text: "A little information about us: We're a full-service property " +
                        'management company designed for owners who prefer to have ' +
                        'someone else run the day-to-day operations. We specialize in ' +
                        'standard long-term rentals with 12-month leases, fully ' +
                        'unfurnished, and rented as an entire unit.',
                    mode: 'verbatim',
                    depth: 0,
                },
                {
                    kind: 'question',
                    id: 'service_fit',
                    text: 'Does that line up with what you are thinking for your property?',
                    answerType: { kind: 'yes_no' },
                    required: true,
                    mode: 'verbatim',
                    depth: 0,
                },
                {
                    kind: 'question',
                    id: 'property_situation',
                    text: "Perfect, thanks. I'd love to learn a little more about the " +
                        'property. Is it your primary home or rented already? Fill me ' +
                        'in on the current situation.',
                    answerType: {
                        kind: 'options',
                        options: ['primary', 'rented', 'vacant', 'other'],
                    },
                    required: false,
                    mode: 'verbatim',
                    depth: 0,
                },
            ],
        },
        {
            id: SCHEDULE_ID,
            type: 'conversation',
            title: 'Schedule a market-expert call',
            position: 3,
            prompt: 'Offer the caller a time to connect with our market expert, and book ' +
                'the time they pick.',
            tools: [{ tool: 'OWNER_MEETING_SCHEDULING' }],
            knowledgeBaseIds: [],
            excludedKnowledgeBaseIds: [],
            // An owner who would rather hear from a person than pick a time leaves
            // for the handoff instead of being pushed through the calendar.
            signals: [
                {
                    id: 'wants_follow_up',
                    text: 'The caller asked for someone from the team to follow up instead ' +
                        'of booking a time now',
                },
            ],
        },
        {
            id: END_CONFIRM_ID,
            type: 'end_conversation',
            title: 'End conversation',
            position: 4,
            signOff: {
                mode: 'exact',
                promptText: '',
                exactText: "You're all set. Thanks for reaching out, and have a great rest of " +
                    'your day.',
            },
        },
        {
            id: END_HANDOFF_ID,
            type: 'end_conversation',
            title: 'End conversation',
            position: 5,
            signOff: {
                mode: 'exact',
                promptText: '',
                exactText: "Thanks for taking the time to chat. I'll have the team follow up " +
                    'with you directly. Have a good one.',
            },
        },
        {
            id: END_DNC_ID,
            type: 'end_conversation',
            title: 'End conversation',
            position: 6,
            signOff: {
                mode: 'exact',
                promptText: '',
                exactText: "Understood. I'll update your contact so you don't get more calls " +
                    'from us. Have a good day.',
            },
        },
    ],
    edges: [
        OPENING.edges[0],
        // Cross-cutting flows, in priority order.
        {
            id: '00000000-0000-4000-8000-00000000e005',
            fromNodeId: null,
            toNodeId: END_DNC_ID,
            condition: { type: 'event', event: SCRATCHPAD_EVENTS.DO_NOT_CALL },
            position: 1,
        },
        { ...OPENING.edges[1], position: 2 },
        {
            id: '00000000-0000-4000-8000-00000000e002',
            fromNodeId: GREET_ID,
            toNodeId: OWNER_LEAD_FORM_NODE_ID,
            condition: { type: 'event', event: RUNTIME_EVENTS.TEXT_DELIVERED },
            position: 3,
        },
        // The caller is not a fit, or gave no usable zip: hand off.
        {
            id: '00000000-0000-4000-8000-00000000e003',
            fromNodeId: OWNER_LEAD_FORM_NODE_ID,
            toNodeId: END_HANDOFF_ID,
            condition: anyOf(answerEquals(OWNER_LEAD_FORM_NODE_ID, 'service_fit', 'no'), questionResult(OWNER_LEAD_FORM_NODE_ID, 'property_zip', 'off_type')),
            position: 4,
        },
        // Block-level fallthrough: everything else ends the discovery block and
        // moves on to scheduling.
        {
            id: '00000000-0000-4000-8000-00000000e010',
            fromNodeId: OWNER_LEAD_FORM_NODE_ID,
            toNodeId: SCHEDULE_ID,
            condition: allOf(formResult(OWNER_LEAD_FORM_NODE_ID, 'completed')),
            position: 5,
        },
        {
            id: '00000000-0000-4000-8000-00000000e006',
            fromNodeId: SCHEDULE_ID,
            toNodeId: END_CONFIRM_ID,
            condition: { type: 'event', event: 'meeting_booked' },
            position: 6,
        },
        {
            id: '00000000-0000-4000-8000-00000000e011',
            fromNodeId: SCHEDULE_ID,
            toNodeId: END_HANDOFF_ID,
            condition: allOf(signalEquals(SCHEDULE_ID, 'wants_follow_up', true)),
            position: 7,
        },
    ],
};
//# sourceMappingURL=owner-lead-template.js.map